// src/components/Channels.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Select, Button, notification } from 'antd';
import { useDispatch } from 'react-redux';
import { createChannel } from '../actions/channelActions';
import './Channels.css';

const { Option } = Select;

const ChannelsSection = ({ components, setIsChannelsSubmitted, applicationId, isTMA }) => {
  const dispatch = useDispatch();
  const [incomingComponentId, setIncomingComponentId] = useState(null);
  const [outgoingComponentId, setOutgoingComponentId] = useState(null);

  const compIds = useMemo(() => (components || []).map(c => c.id), [components]);
  const exactlyTwo = compIds.length === 2;
  const disabledAll = compIds.length < 2;

  const otherOf = (id) => compIds.find(x => x !== id) ?? null;

  // Initialize sensible defaults
  useEffect(() => {
    if (disabledAll) {
      setIncomingComponentId(null);
      setOutgoingComponentId(null);
      return;
    }
    if (!incomingComponentId && !outgoingComponentId) {
      setIncomingComponentId(compIds[0]);
      setOutgoingComponentId(compIds[1]);
      return;
    }
    if (incomingComponentId && !outgoingComponentId) {
      setOutgoingComponentId(otherOf(incomingComponentId));
    } else if (!incomingComponentId && outgoingComponentId) {
      setIncomingComponentId(otherOf(outgoingComponentId));
    }
  }, [compIds, disabledAll, incomingComponentId, outgoingComponentId]);

  // Keep them different
  useEffect(() => {
    if (!incomingComponentId || !outgoingComponentId) return;
    if (incomingComponentId === outgoingComponentId) {
      // When equal, prefer adjusting outgoing
      setOutgoingComponentId(otherOf(incomingComponentId));
    }
  }, [incomingComponentId, outgoingComponentId, compIds]);

  const handleIncomingChange = (val) => {
    setIncomingComponentId(val);
    if (exactlyTwo) {
      // Auto-set the other side to the remaining component
      setOutgoingComponentId(otherOf(val));
    } else if (val === outgoingComponentId) {
      // >2 case: if equal, move outgoing to a different one
      const next = compIds.find(id => id !== val) ?? null;
      setOutgoingComponentId(next);
    }
  };

  const handleOutgoingChange = (val) => {
    setOutgoingComponentId(val);
    if (exactlyTwo) {
      setIncomingComponentId(otherOf(val));
    } else if (val === incomingComponentId) {
      const next = compIds.find(id => id !== val) ?? null;
      setIncomingComponentId(next);
    }
  };

  const handleCreateChannel = () => {
    if (!incomingComponentId || !outgoingComponentId) {
      notification.warning({
        message: 'Missing Required Fields',
        description: 'Please select both incoming and outgoing components.',
      });
      return;
    }
    if (incomingComponentId === outgoingComponentId) {
      notification.warning({
        message: 'Invalid Mapping',
        description: 'A component cannot map to itself.',
      });
      return;
    }

    dispatch(createChannel({ applicationId, incomingComponentId, outgoingComponentId }))
      .then(() => {
        setIsChannelsSubmitted?.(true);
        notification.success({
          message: 'Channel Added Successfully',
          description: 'The channel has been successfully created.',
        });
      })
      .catch((error) => {
        notification.error({
          message: 'Failed to Add Channel',
          description: error?.message || 'Something went wrong!',
        });
      });
  };

  return (
    <div className="channels-section">
      <h5>Channels</h5>

      <Form layout="vertical" onFinish={handleCreateChannel}>
        <Form.Item label="Incoming Component" required>
          <Select
            value={incomingComponentId}
            onChange={handleIncomingChange}
            disabled={disabledAll}
            placeholder="Select incoming component"
          >
            {(components || []).map((c) => (
              <Option
                key={c.id}
                value={c.id}
                // Disable the counterpart only when there are > 2 components.
                disabled={!exactlyTwo && c.id === outgoingComponentId}
              >
                {c.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Outgoing Component" required>
          <Select
            value={outgoingComponentId}
            onChange={handleOutgoingChange}
            disabled={disabledAll}
            placeholder="Select outgoing component"
          >
            {(components || []).map((c) => (
              <Option
                key={c.id}
                value={c.id}
                disabled={!exactlyTwo && c.id === incomingComponentId}
              >
                {c.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          className="add-channel-button"
          disabled={disabledAll}
        >
          Add Channel
        </Button>
      </Form>

      {isTMA && (
        <p style={{ marginTop: 8, color: '#666' }}>
          Channels are optional for TMA users. You may submit without adding channels.
        </p>
      )}
    </div>
  );
};

export default ChannelsSection;
